// helpers
import {
  availabilityIsAvailable,
  availabilityIsBusy,
  availabilityIsClosed,
  availabilityIsUnavailable,
  availabilityIsCheckinDay,
  availabilityIsCheckoutDay,
} from "../../../availability";

// libraries
import EnhancedDate from "../../../../libraries/EnhancedDate";

/** Date du jour. */
const today = new EnhancedDate().startOfDays();

/**
 * Retourne vrai si la durée du séjour respecte la contrainte définie.
 * @param {object} param0
 * @param {object} param0.availability
 * @param {EnhancedDate} param0.start
 * @param {EnhancedDate} param0.date
 * @return {boolean}
 */
function availabilityIsMinDurationValid({ availability, start, date }) {
  const diffDays = (date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

  return diffDays >= availability?.minDuration ?? 0;
}

/**
 * Retourne vrai si la date respecte la date entre l'entrée en location et la réservation.
 * @param {object} param0
 * @param {object} param0.availability
 * @param {EnhancedDate} param0.date
 * @return {boolean}
 */
function availabilityIsMinOffsetValid({ availability, date }) {
  const diffDays = (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);

  return diffDays >= availability?.minOffsets ?? 0;
}

/**
 * Retourne la couleur a appliquer à un jour.
 * @param {object} param0
 * @param {object} param0.availabilitiesData
 * @param {"checkin" | "checkout"} param0.mode
 * @param {EnhancedDate?} param0.date
 * @param {EnhancedDate?} param0.start
 * @param {EnhancedDate?} param0.end
 * @param {EnhancedDate?} param0.hover
 * @param {EnhancedDate?} param0.max
 */
export default function bookingDatePickerGetDay({
  availabilitiesData,
  mode,
  date,
  start,
  end,
  hover,
  max,
}) {
  const day = {
    departureBackgroundColor: "default",
    arrivalBackgroundColor: "default",
    textColor: "default",
    interactive: false,
  };

  if (date.isGreaterOrEqualTo(today) && (!max || date.isLessOrEqualTo(max))) {
    const dateKey = date.format("Y-m-d");
    const previousDateKey = date.clone().addDate(-1).format("Y-m-d");

    if (date.isEqualTo(start) || date.isEqualTo(end)) {
      day.departureBackgroundColor = "interaction";
      day.arrivalBackgroundColor = "interaction";
      day.textColor = "highlight";
      day.interactive = false;
    } else if (date.isGreaterThan(start) && date.isLessThan(hover)) {
      day.departureBackgroundColor = "hover";
      day.arrivalBackgroundColor = "hover";
      day.textColor = "highlight";
      day.interactive =
        "checkout" === mode &&
        availabilityIsAvailable({
          availability: availabilitiesData[previousDateKey],
        });
    } else {
      switch (mode) {
        case "checkin": {
          const isDayAvailable = availabilityIsAvailable({
            availability: availabilitiesData[dateKey],
          });

          if (isDayAvailable) {
            day.interactive = true;
          }

          if (date.isEqualTo(hover) && isDayAvailable) {
            day.departureBackgroundColor = "hover";
            day.arrivalBackgroundColor = "hover";
            day.textColor = "highlight";
          } else {
            const previousDateKey = date.clone().addDate(-1).format("Y-m-d");

            if (isDayAvailable) {
              const isPreviousDayBusy =
                availabilityIsBusy({
                  availability: availabilitiesData[previousDateKey],
                }) ||
                availabilityIsClosed({
                  availability: availabilitiesData[previousDateKey],
                }) ||
                availabilityIsUnavailable({
                  availability: availabilitiesData[previousDateKey],
                });

              if (
                availabilityIsCheckinDay({
                  availability: availabilitiesData[dateKey],
                })
              ) {
                if (isPreviousDayBusy) {
                  day.departureBackgroundColor = "busy";
                } else {
                  day.departureBackgroundColor = "availableWithoutConditions";
                }
                day.arrivalBackgroundColor = "availableWithoutConditions";
                day.textColor = "highlight";
              } else {
                if (isPreviousDayBusy) {
                  day.departureBackgroundColor = "busy";
                } else {
                  day.departureBackgroundColor = "availableWithConditions";
                }
                day.arrivalBackgroundColor = "availableWithConditions";
                day.textColor = "highlight";
              }
            } else {
              const isPreviousDayAvailable = availabilityIsAvailable({
                availability: availabilitiesData[previousDateKey],
              });
              const isDayCheckoutDay = availabilityIsCheckoutDay({
                availability: availabilitiesData[dateKey],
              });
              const isDayBusy =
                availabilityIsBusy({
                  availability: availabilitiesData[dateKey],
                }) ||
                availabilityIsClosed({
                  availability: availabilitiesData[dateKey],
                }) ||
                availabilityIsUnavailable({
                  availability: availabilitiesData[dateKey],
                });

              if (isDayBusy) {
                if (isPreviousDayAvailable) {
                  if (isDayCheckoutDay) {
                    day.departureBackgroundColor = "availableWithoutConditions";
                  } else {
                    day.departureBackgroundColor = "availableWithConditions";
                  }
                } else {
                  day.departureBackgroundColor = "busy";
                }

                day.arrivalBackgroundColor = "busy";
                day.textColor = "highlight";
              }
            }
          }

          break;
        }

        case "checkout": {
          if (date.isGreaterThan(start)) {
            const isDayAvailable = availabilityIsAvailable({
              availability: availabilitiesData[dateKey],
            });
            const isPreviousDayAvailable = availabilityIsAvailable({
              availability: availabilitiesData[previousDateKey],
            });

            if (isPreviousDayAvailable) {
              day.interactive = true;
            }

            if (date.isEqualTo(hover) && isPreviousDayAvailable) {
              day.departureBackgroundColor = "hover";
              day.arrivalBackgroundColor = "hover";
              day.textColor = "highlight";
            } else {
              if (isPreviousDayAvailable) {
                const startKey = start.format("Y-m-d");

                if (
                  availabilityIsCheckoutDay({
                    availability: availabilitiesData[dateKey],
                  }) &&
                  availabilityIsMinDurationValid({
                    availability: availabilitiesData[startKey],
                    date,
                    start,
                  }) &&
                  availabilityIsMinOffsetValid({
                    availability: availabilitiesData[startKey],
                    date,
                  })
                ) {
                  day.arrivalBackgroundColor = "availableWithoutConditions";
                  day.departureBackgroundColor = "availableWithoutConditions";
                  day.textColor = "highlight";
                } else {
                  day.arrivalBackgroundColor = "availableWithConditions";
                  day.departureBackgroundColor = "availableWithConditions";
                  day.textColor = "highlight";
                }
              } else {
                day.departureBackgroundColor = "busy";
                day.textColor = "highlight";

                if (isDayAvailable) {
                  if (
                    availabilityIsCheckinDay({
                      availability: availabilitiesData[dateKey],
                    })
                  ) {
                    day.arrivalBackgroundColor = "availableWithoutConditions";
                  } else {
                    day.arrivalBackgroundColor = "availableWithConditions";
                  }
                } else {
                  day.arrivalBackgroundColor = "busy";
                }
              }
            }
          }

          break;
        }
      }
    }
  }

  return day;
}
